import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';
import {
	BsBoxArrowUpRight,
	BsChevronDown,
	BsGripVertical,
	BsChevronUp,
	BsEyedropper as Eyedropper,
} from 'react-icons/bs';
import * as BsIcons from 'react-icons/bs';

import BaySwitch from '../../../BaySwitch';
import { colorsArr } from '../../../utilities/constants/launchpadConstants';
import IconCategories from '../../Icons/IconCategories';
import ColorPickerPortal from '../ColorPickerPortal';
import './BaseEditWidget.scss';

interface BaseEditWidgetProps {
	t: (key: string) => string;
	editModalInputFields: any; // TODO: Define proper type
	setEditModalInputFields: React.Dispatch<React.SetStateAction<any>>;
	widgetId: string;
	onColorCodesChange?: (colorCodes: any, widgetId: string) => void;
	widgetFinishStatus: any; // TODO: Define proper type
	widgetCustomizationData: any; // TODO: Define proper type
	drawerThemeSettings: any; // TODO: Define proper type
	widgetDrawerThemeSettings: any; // TODO: Define proper type
	setSettingsDefaultClicked: React.Dispatch<React.SetStateAction<any>>;
	handleLinkClick?: (e: React.MouseEvent, linkPath: string) => void;
	widgetType?: string;
	defaultIcon: { name: string; label: string };
	defaultTheme?: string;
	renderCustomContent?: () => React.ReactNode;
}

const BaseEditWidget: React.FC<BaseEditWidgetProps> = ({
	t,
	editModalInputFields,
	setEditModalInputFields,
	widgetId,
	onColorCodesChange,
	widgetFinishStatus,
	widgetCustomizationData,
	drawerThemeSettings,
	widgetDrawerThemeSettings,
	setSettingsDefaultClicked,
	handleLinkClick = () => {},
	widgetType,
	defaultIcon,
	defaultTheme = 'blue',
	renderCustomContent,
}) => {
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);
	const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);
	const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
	const [selectedIcon, setSelectedIcon] = useState({
		name: defaultIcon.name,
		label: defaultIcon.label,
	});
	const [customThemeEnabled, setCustomThemeEnabled] = useState<boolean | null>(null);
	const [selectedTheme, setSelectedTheme] = useState(colorsArr[defaultTheme]);
	const [colorCodes, setColorCodes] = useState<any>({});

	// Local state for immediate color feedback
	const [localColorState, setLocalColorState] = useState<any>({});

	const [activeSection, setActiveSection] = useState<string | null>(null);

	const iconColorRef = useRef<HTMLDivElement>(null);
	const bgColorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setSettingsDefaultClicked((prev) => ({
			...prev,
			[widgetId]: false,
		}));
	}, []);

	const handleIconSelect = ({ name, label }: { name: string; label: string }) => {
		setSelectedIcon({ name, label });
		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				headerIcon: { name, label },
			},
		}));
	};

	useEffect(() => {
		const iconData = editModalInputFields?.[widgetId]?.headerIcon;
		if (iconData) {
			setSelectedIcon({
				name: iconData.name,
				label: iconData.label,
			});
		}
	}, [editModalInputFields?.[widgetId]?.headerIcon, widgetId]);

	const Icon = BsIcons[selectedIcon.name as keyof typeof BsIcons] || BsIcons[defaultIcon.name as keyof typeof BsIcons];

	const handleThemeChange = (key: string) => {
		setCustomThemeEnabled(false);
		setSelectedTheme(colorsArr[key]);
		const newColorCodes = {
			iconColor: colorsArr[key].iconHexColor,
			iconBgColor: colorsArr[key].bgHexColor,
			linkColor: colorsArr[key].iconHexColor,
		};
		setColorCodes(newColorCodes);
		onColorCodesChange?.(newColorCodes, widgetId);
		setOpenDropdown(null);

		// Clear any local color state when selecting a preset theme
		setLocalColorState({});
	};

	const debouncedUpdate = useCallback(
		debounce((value: string, type: string, customThemeVisible: boolean) => {
			if (type && value && customThemeVisible) {
				const updatedColorCodes = {
					...colorCodes,
					[type]: value,
					...(type === 'iconColor' && { linkColor: value }),
				};
				setColorCodes(updatedColorCodes);
				onColorCodesChange?.(updatedColorCodes, widgetId);
			}
		}, 300),
		[colorCodes, onColorCodesChange],
	);

	useEffect(() => {
		const widgetData = widgetFinishStatus?.[widgetId]
			? widgetCustomizationData
			: editModalInputFields?.[widgetId];
		if (widgetData) {
			const iconColor = widgetData.iconColor;
			const iconBgColor = widgetData.iconBgColor;

			const matchingTheme = Object.entries(colorsArr).find(
				([_, theme]: [string, any]) =>
					theme.iconHexColor === iconColor && theme.bgHexColor === iconBgColor,
			);

			if (matchingTheme) {
				setSelectedTheme(matchingTheme[1]);
				const newColorCodes = {
					iconColor: matchingTheme[1].iconHexColor,
					iconBgColor: matchingTheme[1].bgHexColor,
					linkColor: matchingTheme[1].iconHexColor,
				};
				setColorCodes(newColorCodes);
				onColorCodesChange?.(newColorCodes, widgetId);
				setCustomThemeEnabled(false);
			} else {
				setCustomThemeEnabled(true);
				const newColorCodes = {
					iconColor: iconColor || colorsArr[defaultTheme].iconHexColor,
					iconBgColor: iconBgColor || colorsArr[defaultTheme].bgHexColor,
					linkColor: iconColor || colorsArr[defaultTheme].iconHexColor,
				};
				setColorCodes(newColorCodes);
				onColorCodesChange?.(newColorCodes, widgetId);
			}
		}
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				[e.target.name]: e.target.value,
			},
		}));
	};

	const handleSwitchToggle = (linkId: string) => {
		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				links: prevFields[widgetId]?.links?.map((link: any) =>
					link.id === linkId ? { ...link, isVisible: !link.isVisible } : link,
				),
			},
		}));
	};

	const handleSectionToggle = (linkKey: string) => {
		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				sections: prevFields[widgetId].sections.map((section: any) => ({
					...section,
					options: section.options.map((option: any) => ({
						...option,
						links: option.links.map((link: any) =>
							link.key === linkKey
								? { ...link, isVisible: !link.isVisible }
								: link,
						),
					})),
				})),
			},
		}));
	};

	const handleUseSettingsDefault = () => {
		const iconColor =
			widgetDrawerThemeSettings?.iconColor ||
			drawerThemeSettings?.iconColor ||
			colorsArr[defaultTheme].iconHexColor;
		const iconBgColor =
			widgetDrawerThemeSettings?.iconBgColor ||
			drawerThemeSettings?.iconBgColor ||
			colorsArr[defaultTheme].bgHexColor;

		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				allowWidgetCustomization: true,
				iconColor,
				iconBgColor,
				linkColor: iconColor,
			},
		}));
		setOpenDropdown(null);

		const matchingTheme = Object.entries(colorsArr).find(
			([_, theme]: [string, any]) =>
				theme.iconHexColor === iconColor && theme.bgHexColor === iconBgColor,
		);

		if (matchingTheme) {
			setSelectedTheme(matchingTheme[1]);
			const newColorCodes = {
				iconColor: matchingTheme[1].iconHexColor,
				iconBgColor: matchingTheme[1].bgHexColor,
				linkColor: matchingTheme[1].iconHexColor,
			};
			setColorCodes(newColorCodes);
			onColorCodesChange?.(newColorCodes, widgetId);
			setCustomThemeEnabled(false);
		} else {
			setCustomThemeEnabled(true);
			const newColorCodes = {
				iconColor,
				iconBgColor,
				linkColor: iconColor,
			};
			setColorCodes(newColorCodes);
			onColorCodesChange?.(newColorCodes, widgetId);
		}

		// Clear local color state when changing themes
		setLocalColorState({});
	};

	// Icon Picker Overlay
	const iconPickerOverlay = (
		<div className="icon-dropdown-menu rounded-1 p-3 bg-white border border-100 box-shadow">
			{Object.entries(IconCategories).map(([category, icons], catIdx) => (
				<div key={category} className="mb-3">
					<div className="fw-bold text-secondary text-uppercase small mb-2">
						{category}
					</div>
					<div
						className="icon-dropdown-grid"
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(5, 1fr)',
							gap: '8px',
						}}
					>
						{icons.map(({ name }) => {
							const IconComp = BsIcons[name as keyof typeof BsIcons];
							return (
								<div
									key={name}
									className={`icon-dropdown-item d-flex align-items-center justify-content-center p-2 rounded-1 bg-50 cursor-pointer ${selectedIcon.name === name ? ' border border-primary' : ''}`}
									style={{ minWidth: 32, minHeight: 32 }}
									onClick={() => {
										handleIconSelect({
											name,
											label: name
												.replace(/^Bs/, '')
												.replace(/([A-Z])/g, '-$1')
												.toLowerCase()
												.replace(/^-/, ''),
										});
										setOpenDropdown(null);
									}}
								>
									{IconComp && <IconComp size={20} />}
								</div>
							);
						})}
					</div>
					{catIdx < Object.entries(IconCategories).length - 1 && (
						<div className="border-top border-secondary-subtle my-2" />
					)}
				</div>
			))}
		</div>
	);

	// Theme Picker Overlay
	const themePickerOverlay = (
		<div className="theme-dropdown-menu rounded-1 p-3 bg-white border border-100 box-shadow">
			<Form.Label className="fw-bold fs-6 text-secondary m-0">
				{t('presetThemes')}
			</Form.Label>
			<div className="d-flex flex-wrap gap-2 mt-2 mb-2">
				{Object.keys(colorsArr).map((key) => (
					<div
						key={key}
						className={`preset-theme-selector d-flex align-items-center justify-content-center selector cursor-pointer rounded-1 ${colorsArr[key]?.bgColor === selectedTheme?.bgColor ? 'border border-primary' : ''}`}
						style={{ width: 24, height: 24 }}
						onClick={() => {
							handleThemeChange(key);
							setOpenDropdown(null);
						}}
					>
						<div
							className={`edit-widget-color-preview rounded-1 ${colorsArr[key].colorName === 'Dark Mode' ? 'border border-200' : colorsArr[key]?.iconHexColor}`}
							style={{
								backgroundColor:
									colorsArr[key].colorName === 'Light Mode'
										? '#191C29'
										: colorsArr[key].colorName === 'Dark Mode'
											? '#FFFFFF'
											: colorsArr[key].iconHexColor,
								width: 20,
								height: 20,
							}}
						/>
					</div>
				))}
			</div>
			<div className="border-top border-secondary-subtle my-2" />
			<div className="d-flex flex-column gap-2">
				<div
					className="d-flex align-items-center gap-2 text-primary cursor-pointer"
					onClick={() => {
						setCustomThemeEnabled(!customThemeEnabled);
						setOpenDropdown(null);
						// Reset local color state when toggling custom theme
						setLocalColorState({});
					}}
				>
					<Eyedropper size={16} />
					<span className="text-primary">{t('customTheme')}</span>
				</div>
				<div
					className="d-flex align-items-center gap-2 text-primary cursor-pointer"
					onClick={() => {
						handleUseSettingsDefault();
						// Reset local color state when using settings default
						setLocalColorState({});
					}}
				>
					<div
						className="settings-default"
						style={{
							backgroundColor:
								widgetDrawerThemeSettings?.iconColor ||
								drawerThemeSettings?.iconColor ||
								colorsArr['blue'].iconHexColor,
						}}
					/>
					<span className="text-primary">{t('useSettingsDefault')}</span>
				</div>
			</div>
		</div>
	);

	const handleDropdownToggle = (dropdown: string) => {
		if (dropdown === 'iconColor' || dropdown === 'bgColor') {
			// For color pickers, use the dedicated color picker handling
			return;
		}
		setOpenDropdown((prev) => (prev === dropdown ? null : dropdown));
	};

	const handleColorPickerOpen = (type: string, ref: React.RefObject<HTMLDivElement>) => {
		if (openColorPicker === type) {
			setOpenColorPicker(null);
			return;
		}

		const rect = ref.current?.getBoundingClientRect();
		if (rect) {
			const position = {
				top: rect.bottom + 8,
				left: rect.left,
			};
			setPickerPosition(position);
			setOpenColorPicker(type);
		}
	};

	const handleColorChange = (value: string, type: string) => {
		// For immediate visual feedback
		const mappedType = type === 'bgColor' ? 'iconBgColor' : type;

		// Update local state for immediate visual feedback
		setLocalColorState((prev: any) => ({
			...prev,
			[mappedType]: value,
		}));
	};

	const handleColorChangeComplete = (value: string, type: string) => {
		// Map bgColor to iconBgColor for consistency with state property names
		const mappedType = type === 'bgColor' ? 'iconBgColor' : type;
		debouncedUpdate(value, mappedType, customThemeEnabled || false);
	};

	const handleDragStart = (e: React.DragEvent, linkId: string) => {
		e.dataTransfer.setData('text/plain', linkId);
		(e.target as HTMLElement).style.opacity = '0.4';
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
	};

	const handleDrop = (e: React.DragEvent, targetLinkId: string) => {
		e.preventDefault();
		const sourceLinkId = e.dataTransfer.getData('text/plain');
		if (sourceLinkId === targetLinkId) return;

		const oldIndex = editModalInputFields[widgetId].links.findIndex(
			(link: any) => link.id === sourceLinkId,
		);
		const newIndex = editModalInputFields[widgetId].links.findIndex(
			(link: any) => link.id === targetLinkId,
		);

		const newLinks = [...editModalInputFields[widgetId].links];
		const [movedItem] = newLinks.splice(oldIndex, 1);
		newLinks.splice(newIndex, 0, movedItem);

		setEditModalInputFields((prev: any) => ({
			...prev,
			[widgetId]: {
				...prev[widgetId],
				links: newLinks,
			},
		}));
	};

	const handleDragEnd = (e: React.DragEvent) => {
		(e.target as HTMLElement).style.opacity = '1';
	};

	const toggleLinkAvailability = (sectionId: string) => {
		setActiveSection((prevSectionId) =>
			prevSectionId === sectionId ? '' : sectionId,
		);
	};

	const handleMarketingDragOver = (e: React.DragEvent) => e.preventDefault();

	const handleMarketingDragStart = (e: React.DragEvent, data: any) => {
		e.dataTransfer.setData('text/plain', JSON.stringify(data));
		(e.target as HTMLElement).style.opacity = '0.4';
	};

	const handleMarketingDrop = (e: React.DragEvent, targetData: any) => {
		e.preventDefault();
		const sourceData = JSON.parse(e.dataTransfer.getData('text/plain'));

		setEditModalInputFields((prev: any) => {
			const prevSections = prev[widgetId].sections;

			// Handle link reordering
			if (sourceData.type === 'link' && targetData.type === 'link') {
				if (
					sourceData.sectionId !== targetData.sectionId ||
					sourceData.optionTitle !== targetData.optionTitle
				)
					return prev;

				return {
					...prev,
					[widgetId]: {
						...prev[widgetId],
						sections: prevSections.map((section: any) => {
							if (section.id !== sourceData.sectionId) return section;

							return {
								...section,
								options: section.options.map((option: any) => {
									if (option.title !== sourceData.optionTitle) return option;

									const links = [...option.links];
									const fromIndex = links.findIndex(
										(l: any) => l.key === sourceData.linkId,
									);
									const toIndex = links.findIndex(
										(l: any) => l.key === targetData.linkId,
									);

									if (fromIndex === -1 || toIndex === -1) return option;

									const [moved] = links.splice(fromIndex, 1);
									links.splice(toIndex, 0, moved);

									return {
										...option,
										links,
									};
								}),
							};
						}),
					},
				};
			}

			// Handle option reordering
			if (sourceData.type === 'option' && targetData.type === 'option') {
				if (sourceData.sectionId !== targetData.sectionId) return prev;

				return {
					...prev,
					[widgetId]: {
						...prev[widgetId],
						sections: prevSections.map((section: any) => {
							if (section.id !== sourceData.sectionId) return section;

							const options = [...section.options];
							const fromIndex = options.findIndex(
								(o: any) => o.title === sourceData.optionTitle,
							);
							const toIndex = options.findIndex(
								(o: any) => o.title === targetData.optionTitle,
							);

							if (fromIndex === -1 || toIndex === -1) return section;

							const [moved] = options.splice(fromIndex, 1);
							options.splice(toIndex, 0, moved);

							return {
								...section,
								options,
							};
						}),
					},
				};
			}

			// Handle section reordering
			if (sourceData.type === 'section' && targetData.type === 'section') {
				const sections = [...prevSections];
				const fromIndex = sections.findIndex(
					(s: any) => s.id === sourceData.sectionId,
				);
				const toIndex = sections.findIndex(
					(s: any) => s.id === targetData.sectionId,
				);

				if (fromIndex === -1 || toIndex === -1) return prev;

				const [moved] = sections.splice(fromIndex, 1);
				sections.splice(toIndex, 0, moved);

				return {
					...prev,
					[widgetId]: {
						...prev[widgetId],
						sections,
					},
				};
			}

			return prev;
		});
	};

	const handleMarketingDragEnd = (e: React.DragEvent) => {
		(e.target as HTMLElement).style.opacity = '1';
	};

	return (
		<div className="edit-widget-container d-flex flex-column gap-3">
			<div className="w-100 d-flex flex-row justify-content-between gap-3">
				{/* Icon Picker */}
				<div className="w-50 d-flex flex-column gap-2">
					<Form.Label className="fw-bold fs-6 text-dark m-0">
						{t('icon')}
					</Form.Label>
					<OverlayTrigger
						rootClose
						container={document.body}
						overlay={iconPickerOverlay}
						placement="bottom-start"
						show={openDropdown === 'icon'}
						onHide={() => setOpenDropdown(null)}
					>
						{({ ref, ...triggerHandler }) => (
							<div
								ref={ref as any}
								{...triggerHandler}
								className="edit-widget-input d-flex align-items-center form-control p-1 pe-2 cursor-pointer"
								onClick={() => handleDropdownToggle('icon')}
							>
								<div className="d-flex align-items-center gap-2 flex-1">
									<div className="edit-widget-icon-picker bg-50 d-flex align-items-center justify-content-center rounded-1">
										{Icon && <Icon size={16} />}
									</div>
									<span className="ms-2 text-dark fw-normal text-capitalize">
										{selectedIcon.label}
									</span>
								</div>
								<div
									className="mx-2"
									style={{ width: 1, height: 28, background: '#E0D7F7' }}
								/>
								<BsChevronDown />
							</div>
						)}
					</OverlayTrigger>
				</div>
				{/* Icon Color Theme Picker */}
				<div className="w-50 d-flex flex-column gap-2">
					<Form.Label className="fw-bold fs-6 text-dark m-0">
						{t('colorTheme')}
					</Form.Label>
					<OverlayTrigger
						rootClose
						container={document.body}
						overlay={themePickerOverlay}
						placement="bottom-start"
						show={openDropdown === 'theme'}
						onHide={() => setOpenDropdown(null)}
					>
						{({ ref, ...triggerHandler }) => (
							<div
								ref={ref as any}
								{...triggerHandler}
								className="edit-widget-input d-flex flex-row align-items-center form-control p-1 pe-2 cursor-pointer"
								onClick={() => handleDropdownToggle('theme')}
							>
								<div className="d-flex align-items-center gap-2 flex-1">
									<div
										className="edit-widget-color-picker d-flex align-items-center justify-content-center rounded-1"
										style={{
											backgroundColor: customThemeEnabled
												? localColorState.iconColor || colorCodes?.iconColor
												: selectedTheme?.colorName === 'Light Mode'
													? '#191C29'
													: selectedTheme?.colorName === 'Dark Mode'
														? '#FFFFFF'
														: selectedTheme?.iconHexColor,
											width: 24,
											height: 24,
											border: '4px solid var(--bs-50)',
										}}
									>
										{/* Empty for color swatch */}
									</div>
									<span className="ms-2 text-dark fw-normal">
										{customThemeEnabled
											? 'Custom Theme'
											: selectedTheme?.colorName
												? `${selectedTheme.colorName}${selectedTheme.isDefault ? ' (Settings Default)' : ''}`
												: ''}
									</span>
								</div>
								<div
									className="mx-2"
									style={{ width: 1, height: 28, background: '#E0D7F7' }}
								/>
								<BsChevronDown />
							</div>
						)}
					</OverlayTrigger>
				</div>
			</div>
			{/* Custom Theme Color Pickers */}
			{customThemeEnabled && (
				<div className="w-100 d-flex flex-row justify-content-between gap-3">
					<div className="w-50 d-flex flex-column gap-2">
						<Form.Label className="fw-bold fs-6 mb-0 text-dark">
							{t('iconColor')}
						</Form.Label>
						<div
							className="edit-widget-input d-flex flex-row gap-2 align-items-center form-control p-1 cursor-pointer color-swatch-click-target"
							onClick={() => handleColorPickerOpen('iconColor', iconColorRef)}
						>
							<div
								ref={iconColorRef}
								className="color-swatch"
								style={{
									backgroundColor:
										localColorState.iconColor || colorCodes?.iconColor,
									width: 24,
									height: 24,
									borderRadius: 4,
									cursor: 'pointer',
									border: '4px solid var(--bs-50)',
								}}
							/>
							<div className="fs-6 fw-normal">
								{localColorState.iconColor || colorCodes?.iconColor}
							</div>
						</div>
					</div>
					<div className="w-50 d-flex flex-column gap-2">
						<Form.Label className="fw-bold fs-6 mb-0 text-dark">
							{t('iconBgColor')}
						</Form.Label>
						<div
							className="edit-widget-input d-flex flex-row gap-2 align-items-center form-control p-1 cursor-pointer color-swatch-click-target"
							onClick={() => handleColorPickerOpen('bgColor', bgColorRef)}
						>
							<div
								ref={bgColorRef}
								className="color-swatch"
								style={{
									backgroundColor:
										localColorState.iconBgColor || colorCodes?.iconBgColor,
									width: 24,
									height: 24,
									borderRadius: 4,
									cursor: 'pointer',
									border: '4px solid var(--bs-50)',
								}}
							/>
							<div className="fs-6 fw-normal">
								{localColorState.iconBgColor || colorCodes?.iconBgColor}
							</div>
						</div>
					</div>
				</div>
			)}
			{/* Icon Examples */}
			<div className="d-flex flex-column gap-2">
				<Form.Label className="fw-normal fs-6 text-dark m-0">
					{t('iconExamples')}:
				</Form.Label>
				<div className="d-flex w-100 gap-2">
					{/* Example: show a few icons with the current color */}
					{[
						'BsPeople',
						'BsStar',
						'BsBell',
						'BsHouse',
						'BsGear',
						'BsMegaphone',
						'BsDoorOpen',
						'BsSliders',
						'BsCamera',
						'BsGlobe',
					].map((iconName) => {
						const ExampleIcon = BsIcons[iconName as keyof typeof BsIcons];
						return ExampleIcon ? (
							<div
								key={iconName}
								className="edit-widget-icon-example d-flex justify-content-center align-items-center rounded-1 p-2"
								style={{
									backgroundColor:
										localColorState.iconBgColor || colorCodes?.iconBgColor,
								}}
							>
								<ExampleIcon
									color={localColorState.iconColor || colorCodes?.iconColor}
									size={16}
								/>
							</div>
						) : null;
					})}
				</div>
			</div>
			{/* Title Input */}
			<Form.Group className="mb-2 position-relative">
				<Form.Label>
					Title<span className="text-danger">*</span>
				</Form.Label>
				<Form.Control
					required
					className={`edit-widget-input ${!editModalInputFields?.[widgetId]?.headerTitle || editModalInputFields?.[widgetId]?.headerTitle?.length > 16 ? 'is-invalid' : ''}`}
					name="headerTitle"
					placeholder="Enter..."
					type="text"
					value={editModalInputFields?.[widgetId]?.headerTitle || ''}
					onChange={handleInputChange}
				/>
				<span
					className={`edit-widget-counter position-absolute small fw-bold ${editModalInputFields?.[widgetId]?.headerTitle?.length > 16 ? 'text-danger' : 'text-muted'}`}
				>
					{editModalInputFields?.[widgetId]?.headerTitle?.length || 0}/16
				</span>
				{editModalInputFields?.[widgetId]?.headerTitle?.length > 16 && (
					<span className="text-danger small mt-1">
						Must be less than 16 characters
					</span>
				)}
				{!editModalInputFields?.[widgetId]?.headerTitle && (
					<span className="text-danger fw-normal mt-1">
						This field is required
					</span>
				)}
			</Form.Group>
			{/* Subtitle Input */}
			{widgetType !== 'marketing' && (
				<Form.Group className="mb-2 position-relative">
					<Form.Label>
						Subtitle<span className="text-danger">*</span>
					</Form.Label>
					<Form.Control
						required
						className={`edit-widget-input ${!editModalInputFields?.[widgetId]?.headerTitleDescription || editModalInputFields?.[widgetId]?.headerTitleDescription?.length > 36 ? 'is-invalid' : ''}`}
						name="headerTitleDescription"
						placeholder="Enter..."
						type="text"
						value={
							editModalInputFields?.[widgetId]?.headerTitleDescription || ''
						}
						onChange={handleInputChange}
					/>
					<span
						className={`edit-widget-counter position-absolute small fw-bold ${editModalInputFields?.[widgetId]?.headerTitleDescription?.length > 36 ? 'text-danger' : 'text-muted'}`}
					>
						{editModalInputFields?.[widgetId]?.headerTitleDescription?.length ||
							0}
						/36
					</span>
					{editModalInputFields?.[widgetId]?.headerTitleDescription?.length >
						36 && (
						<span className="text-danger small mt-1">
							Must be less than 36 characters
						</span>
					)}
					{!editModalInputFields?.[widgetId]?.headerTitleDescription && (
						<span className="text-danger fw-normal mt-1">
							This field is required
						</span>
					)}
				</Form.Group>
			)}
			{/* Available Links Section */}
			<div className="w-100 border-top border-secondary-subtle" />
			<p className="fw-semibold mb-0 text-dark fs-6 ps-1">
				{t('availableLinks')}
			</p>
			<div className="links-container">
				{widgetType === 'marketing'
					? editModalInputFields?.[widgetId]?.sections?.map((section: any) => (
						<div key={section?.id}>
							<div
								className="d-flex flex-row gap-3 mt-3"
								draggable
								onDragEnd={handleMarketingDragEnd}
								onDragOver={(e) => {
									e.stopPropagation();
									handleMarketingDragOver(e);
								}}
								onDragStart={(e) => {
									e.stopPropagation();
									handleMarketingDragStart(e, {
										type: 'section',
										sectionId: section?.id,
									});
								}}
								onDrop={(e) => {
									e.stopPropagation();
									handleMarketingDrop(e, {
										type: 'section',
										sectionId: section?.id,
									});
								}}
							>
								<BsChevronUp
									className="link-toggle-icon"
									color="#5D5F69"
									size={16}
									style={{
										transform:
												activeSection === section?.id
													? 'rotate(0deg)'
													: 'rotate(180deg)',
									}}
									onClick={() => toggleLinkAvailability(section?.id)}
								/>
								<BsGripVertical
									className="cursor-grab"
									color="#5D5F69"
									size={16}
								/>
								<span className="fw-semibold fs-6 lh-sm">
									{section?.header}
								</span>
							</div>

							<div
								className={`link-section w-100 d-${activeSection === section?.id ? 'flex' : 'none'} flex-column gap-5 mt-3`}
							>
								{section?.options.map((option: any) => (
									<div
										key={option.title}
										className="link-subtitle d-flex flex-column gap-4"
									>
										<div
											className="d-flex flex-row align-items-center gap-2"
											draggable
											onDragEnd={handleMarketingDragEnd}
											onDragOver={(e) => {
												e.stopPropagation();
												handleMarketingDragOver(e);
											}}
											onDragStart={(e) => {
												e.stopPropagation();
												handleMarketingDragStart(e, {
													type: 'option',
													sectionId: section?.id,
													optionTitle: option?.title,
												});
											}}
											onDrop={(e) => {
												e.stopPropagation();
												handleMarketingDrop(e, {
													type: 'option',
													sectionId: section?.id,
													optionTitle: option?.title,
												});
											}}
										>
											<BsGripVertical
												className="cursor-grab"
												color="#5D5F69"
												size={16}
											/>
											<span className="fw-semibold text-secondary text-uppercase small lh-sm">
												{option?.title}
											</span>
										</div>

										{option?.links.map((link: any) => (
											<div
												key={link.key}
												className="d-flex flex-row align-items-center justify-content-between w-100 gap-2"
											>
												<div
													className="d-flex flex-row ps-4 gap-2"
													draggable
													onDragEnd={handleMarketingDragEnd}
													onDragOver={(e) => {
														e.stopPropagation();
														handleMarketingDragOver(e);
													}}
													onDragStart={(e) => {
														e.stopPropagation();
														handleMarketingDragStart(e, {
															type: 'link',
															sectionId: section?.id,
															optionTitle: option?.title,
															linkId: link?.key,
														});
													}}
													onDrop={(e) => {
														e.stopPropagation();
														handleMarketingDrop(e, {
															type: 'link',
															sectionId: section?.id,
															optionTitle: option?.title,
															linkId: link?.key,
														});
													}}
												>
													<BsGripVertical
														className="cursor-grab"
														color="#5D5F69"
														size={16}
													/>
													<span className="link-label fw-semibold text-dark lh-sm">
														{link?.label}
													</span>
												</div>
												<div className="d-flex flex-row align-items-center gap-2">
													<BsBoxArrowUpRight
														className="mt-n2 cursor-pointer"
														size={16}
														onClick={(e) => handleLinkClick(e, link?.path)}
													/>
													<BaySwitch
														checked={link.isVisible}
														id={link.key}
														label=""
														name={link.key}
														onChange={() => handleSectionToggle(link.key)}
													/>
												</div>
											</div>
										))}
									</div>
								))}
							</div>
						</div>
					))
					: editModalInputFields?.[widgetId]?.links?.map((link: any) => (
						<div
							key={link.id}
							draggable
							className="d-flex flex-row justify-content-between align-items-center w-100 mb-2"
							onDragEnd={handleDragEnd}
							onDragOver={handleDragOver}
							onDragStart={(e) => handleDragStart(e, link.id)}
							onDrop={(e) => handleDrop(e, link.id)}
						>
							<div className="d-flex flex-row edit-widget-link-drag-handle align-items-center gap-2 cursor-grab">
								<BsGripVertical color="#5D5F69" size={16} />
								<span className="edit-widget-link-label fw-semibold fs-6">
									{link?.label}
								</span>
							</div>
							<div className="d-flex flex-row align-items-center gap-3">
								<BsBoxArrowUpRight
									className="mt-n2 cursor-pointer"
									color="#0D6EFD"
									size={16}
									onClick={(e) => handleLinkClick(e, link?.path)}
								/>
								<BaySwitch
									checked={link.isVisible}
									id={link.key}
									label=""
									name={link.key}
									onChange={() => handleSwitchToggle(link.id)}
								/>
							</div>
						</div>
					))}
			</div>
			{renderCustomContent && renderCustomContent()}
			<ColorPickerPortal
				color={
					openColorPicker === 'iconColor'
						? colorCodes?.iconColor
						: colorCodes?.iconBgColor
				}
				isOpen={!!openColorPicker}
				position={pickerPosition}
				onChange={(color: string) => handleColorChange(color, openColorPicker as string)}
				onChangeComplete={(color: string) =>
					handleColorChangeComplete(color, openColorPicker as string)
				}
				onClose={() => setOpenColorPicker(null)}
			/>
		</div>
	);
};

export default BaseEditWidget;
